import { HTMLProps } from "@/types";
import { forwardRef, useId } from "react";
import Icon from "@/components/ui/Icon";
import * as RadixSelect from "@radix-ui/react-select";
import Feild, { FeildProps } from "./Feild";

type SelectProps = {
  name: string;
  required?: boolean;
  onChange?: (val: string) => void;
  setValue?: (val: string) => void;
  children?: React.ReactNode;
  id?: string;
} & HTMLProps<"select">;

type Ref = HTMLSelectElement;

type AllProps = React.ComponentPropsWithoutRef<"select"> & SelectProps & Omit<FeildProps, "id">;

const Select = forwardRef<Ref, AllProps>(function (
  {
    placeholder,
    hint,
    label,
    name,
    required,
    prefix,
    suffix,
    disabled,
    errorMessage,
    hasErrorMessage = true,
    setValue,
    onChange,
    id: idFromProps,
    children,
    ...selectProps
  },
  ref
) {
  const generatedIdId = useId();
  const id = idFromProps ?? generatedIdId;

  return (
    <Feild
      hint={hint}
      label={label}
      errorMessage={errorMessage}
      hasErrorMessage={hasErrorMessage}
      disabled={disabled}
      suffix={suffix}
      prefix={prefix}
      required={required}
      name={name}
      id={id}
    >
      <SelectElement {...{ placeholder, disabled, required, name, onChange, ...selectProps }}>{children}</SelectElement>
    </Feild>
  );
});

Select.displayName = "Select";

const SelectElement = forwardRef<Ref, SelectProps>(function ({ onChange, ...props }, ref) {
  return (
    <RadixSelect.Root onValueChange={onChange} value={props?.value}>
      <RadixSelect.Trigger
        ref={ref}
        className={`inline-flex items-baseline rd-2 px-2 py-2.5 leading-none gap-4 b-1 b-sand5 hover:bg-sand3  data-[placeholder]:c-sand11 outline-transparent sm:min-w-45 min-w-9  lt-sm:justify-center
        focus-within:outline-brand-orange focus-within:outline-1px focus-within:outline-solid
        `}
      >
        <RadixSelect.Value placeholder={props?.placeholder} />
        <RadixSelect.Icon className="mis-auto">
          <Icon name="bf-i-ph-caret-down" subdued={false} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          align="start"
          position="popper"
          sideOffset={1}
          className="box-border overflow-hidden bg-[#FCF9F7] rd-3 shd-tinted-3 "
        >
          <RadixSelect.ScrollUpButton>
            <Icon name="bf-i-ph-caret-up" />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="p-2 min-w-45">{props.children}</RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton>
            <Icon name="bf-i-ph-caret-down" />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
});

const Option = ({ children, className, value, ...props }: RadixSelect.SelectItemProps) => {
  return (
    <RadixSelect.Item
      className={`
        leading-none  rd-2 flex items-center gap-6  px-2 py-2 relative select-none
        data-[disabled]:c-melow
        data-[disabled]:pointer-events-none
        data-[highlighted]:outline-none
        data-[highlighted]:bg-sand4
        data-[highlighted]:c-sand12
        ${className}`}
      value={value}
      {...props}
    >
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      <RadixSelect.ItemIndicator className="mis-auto inline-flex items-center justify-center">
        <Icon name="bf-i-ph-check" />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
};

SelectElement.displayName = "SelectElement";
Option.displayName = "Option";

export default Object.assign(Select, {
  Option: Option,
});
